import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Divider, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { format, isYesterday } from "date-fns";
import { Methods } from "enums/Methods";
import { NumberField } from "features/WorklistTools/shared/components/NumberField";
import { downloadWorklist } from "features/WorklistTools/shared/WorklistHelpers";
import { Form, FormikProvider, useFormik } from "formik";
import {
  DashboardTask,
  Maybe,
  MethodReservation,
  OperationNames,
  TaskTypeEnum,
  useGetFeedGroupByPersonalTaskIdQuery,
  useGetFeedGroupPlatesQuery,
  useGetLabwareTypesQuery,
  useGetMethodReservationByPersonalTaskIdQuery,
  useGetPlateBarcodePreviewMutation,
  useReserveMethodMutation,
  WorklistInfoInput,
  WorklistToolMethodReservationInput,
} from "graphql/generated/schema-types";
import {
  getPlateCodeFromPlateBarcode,
  getRunIdFromPlateBarcode,
} from "helpers/plate-barcode-helpers";
import {
  replaceSpacesWithUnderscores,
  sanitizeWindowsFileName,
} from "helpers/string-helpers";
import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { removeNumberOfPlatesFromTaskName } from "../helpers/task-helpers";
import { GeneralFeedFooter } from "./GeneralFeedFooter";
import { GeneralFeedTable } from "./GeneralFeedTable";

interface GeneralFeedProps {
  selectedTask: DashboardTask;
}

export const GeneralFeed = ({ selectedTask }: GeneralFeedProps) => {
  const plates =
    useGetFeedGroupPlatesQuery({
      variables: {
        personalTaskId: selectedTask.taskId,
      },
    }).data?.feedGroupPlates ?? [];

  const labwareTypes =
    useGetLabwareTypesQuery({
      variables: {
        where: {
          isActive: { eq: true },
          plateLayoutTypeId: { in: [2, 3, 4, 7] },
        },
      },
    }).data?.labwareTypes ?? [];

  const methodReservationQuery = useGetMethodReservationByPersonalTaskIdQuery({
    variables: {
      personalTaskId: selectedTask.taskId,
    },
  });
  const methodReservation = methodReservationQuery.data
    ?.methodReservation as MethodReservation[];
  const methodReserved = methodReservation && methodReservation.length > 0;

  const isEB_FEED = selectedTask.methodId === Methods.EB_Feed;
  const isMedEx1224 = selectedTask.methodId === Methods.Media_Exchange_12_24w;

  const feedGroupQuery = useGetFeedGroupByPersonalTaskIdQuery({
    variables: {
      personalTaskId: selectedTask.taskId,
    },
    fetchPolicy: "network-only",
    skip: selectedTask.taskType !== TaskTypeEnum.Personal || !isMedEx1224,
  });
  const includeMystRack =
    isMedEx1224 &&
    (feedGroupQuery.data?.feedGroupByPersonalTaskId?.[0]?.generateMystRack ??
      false);
  const mystFlagError = isMedEx1224 && !!feedGroupQuery.error;

  const getInitialValues = () => {
    if (methodReserved) {
      console.log(methodReservation[0].worklist?.worklistContents);
      const feedWorklistContents =
        methodReservation[0].worklist?.worklistContents
          ?.filter((item) => item?.sourcePlateBarcode !== null)
          .map((row) => {
            return {
              plateBarcode: row?.sourcePlateBarcode ?? "",
              labwareTypeCode: row?.sourcePlateType ?? "",
              dateCreated: "",
              aspirateVol: parseInt(row?.aspirateVol ?? "0"),
              dispenseVol: parseInt(row?.dispenseVol ?? "0"),
            };
          });
      return feedWorklistContents ?? [];
    }

    return plates.map((item) => {
      return {
        plateBarcode: item?.plateBarcode,
        labwareTypeCode: item?.labwareType?.labwareTypeCode,
        dateCreated: item?.dateCreated,
        aspirateVol: isEB_FEED
          ? getAspirateEBFeedVolume(item?.dateCreated)
          : null,
        dispenseVol: isEB_FEED ? 195 : null,
      };
    });
  };

  const getAspirateEBFeedVolume = (dateCreated: string) => {
    return isYesterday(new Date(dateCreated)) ? 210 : 208; //Temporary hardcoded volumes for EB Feed method, can be updated to be dynamic in the future if needed
  };

  const getMethodName = (methodId: Maybe<number> | undefined) => {
    switch (methodId) {
      case Methods.EB_Feed:
        return "EBFeed";
      case Methods.Media_Exchange_12_24w:
      case Methods.Media_Exchange_96w:
        return "MedEx";
    }
  };

  const makeValidationSchema = (labwareTypes: any[]) => {
    const plateSchema = Yup.object().shape({
      plateBarcode: Yup.string().required("Plate barcode is required"),
      labwareTypeCode: Yup.string().required("Labware type is required"),
      aspirateVol: Yup.number()
        .transform((v, ov) => (ov === "" || ov == null ? null : v))
        .nullable()
        .required("Aspirate volume is required")
        .test("max-aspirate-by-labware", "", function (value) {
          if (value == null) return true;

          const { labwareTypeCode } = this.parent as {
            labwareTypeCode?: string;
          };
          const match = labwareTypes.find(
            (t) => t.labwareTypeCode === labwareTypeCode,
          );
          const max = match?.maxAspirateVolume;
          // choose behavior if not found:
          if (max == null) return true; // or: const max = match?.maxAspirateVol ?? 275;

          return value <= max
            ? true
            : this.createError({ message: `Must be ≤ ${max}` });
        }),
      dispenseVol: Yup.number()
        .transform((v, ov) => (ov === "" || ov == null ? null : v))
        .nullable()
        .required("Dispense volume is required")
        .test("max-dispense-by-labware", "", function (value) {
          if (value == null) return true;

          const { labwareTypeCode } = this.parent as {
            labwareTypeCode?: string;
          };
          const match = labwareTypes.find(
            (t) => t.labwareTypeCode === labwareTypeCode,
          );
          const max = match?.maxDispenseVolume;

          if (max == null) return true;

          return value <= max
            ? true
            : this.createError({ message: `Must be ≤ ${max}` });
        }),
    });

    return Yup.object().shape({
      fileName: Yup.string()
        .required("File name is required")
        .max(60, "File name must be 60 characters or less"),
      plates: Yup.array().of(plateSchema),
    });
  };

  const validationSchema = useMemo(
    () => makeValidationSchema(labwareTypes),
    [labwareTypes],
  );

  const now = useMemo(() => new Date(), [selectedTask.taskId]);
  const feedGroupName = removeNumberOfPlatesFromTaskName(
    selectedTask.taskName ?? "",
    plates.length,
  );
  const methodName = getMethodName(selectedTask.methodId);

  const fileNameInitial = sanitizeWindowsFileName(
    `Sys${selectedTask.systemId}_${methodName}_${replaceSpacesWithUnderscores(
      feedGroupName,
    )}_${format(now, "yyyyMMddHHmm")}`,
  );

  const formik = useFormik({
    initialValues: {
      fileName: fileNameInitial,
      plates: getInitialValues(),
    },
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async () => {
      await generateWorklist();
      setSubmitting(false);
    },
    validationSchema,
  });

  const {
    values,
    setValues,
    setFieldValue,
    handleSubmit,
    isValid,
    setSubmitting,
    isSubmitting,
    errors,
  } = formik;
  const [reserveMethod, { loading }] = useReserveMethodMutation();
  const [getPlateBarcodePreview] = useGetPlateBarcodePreviewMutation();

  const getNextAvailableMystBarcode = async (
    sourceBarcodes: string[],
  ): Promise<string> => {
    if (
      sourceBarcodes.length === 0 ||
      new Set(sourceBarcodes.map(getRunIdFromPlateBarcode)).size > 1
    ) {
      throw new Error(
        "All plates in the feed group must be of the same Run ID to generate a MYST rack",
      );
    }

    let barcode: Maybe<string> | undefined;
    try {
      const res = await getPlateBarcodePreview({
        variables: {
          methodId: Methods.Media_Exchange_12_24w,
          sourcePlateBarcodes: [sourceBarcodes[0]],
          createMystPlate: true,
        },
      });
      barcode = res.data?.plateBarcodePreview?.find(
        (plate) => plate?.plateType === "MYST",
      )?.plateBarcode;
    } catch {}
    if (!barcode) throw new Error("Could not generate a MYST rack barcode");
    return barcode;
  };

  // Before WL generation: Get MYST barcode on open, After WL generation: read off the saved HarvestMediaToTubes row.
  const [mystBarcode, setMystBarcode] = useState<string | null>(null);
  const [mystRackError, setMystRackError] = useState<string | null>(null);
  const [mystRackLoading, setMystRackLoading] = useState(false);

  const sortedSourceBarcodes = plates
    .map((plate) => plate?.plateBarcode)
    .filter((barcode): barcode is string => !!barcode)
    .sort();
  // plates is a new array every render, so the effect below watches this string instead
  const sourceBarcodesKey = sortedSourceBarcodes.join(",");

  useEffect(() => {
    setMystBarcode(null);
    setMystRackError(null);
    if (
      !includeMystRack ||
      methodReservationQuery.loading ||
      methodReserved ||
      !sourceBarcodesKey
    )
      return;

    let cancelled = false;
    setMystRackLoading(true);
    getNextAvailableMystBarcode(sortedSourceBarcodes)
      .then((barcode) => !cancelled && setMystBarcode(barcode))
      .catch((e: Error) => !cancelled && setMystRackError(e.message))
      .finally(() => !cancelled && setMystRackLoading(false));
    return () => {
      cancelled = true;
      setMystRackLoading(false);
    };
  }, [
    includeMystRack,
    methodReservationQuery.loading,
    methodReserved,
    sourceBarcodesKey,
  ]);

  const savedMystBarcode =
    methodReservation?.[0]?.worklist?.worklistContents?.find(
      (row) => row?.task === "HarvestMediaToTubes",
    )?.destinationPlateBarcode;

  const displayedMystBarcode = methodReserved ? savedMystBarcode : mystBarcode;
  const showMystBarcode = methodReserved
    ? !!savedMystBarcode
    : includeMystRack || mystFlagError;
  const mystMessageError = mystFlagError
    ? "Could not check whether this feed group generates a MYST rack"
    : mystRackError;

  const buildWorklist = (mystBarcode: string | null) => {
    const worklist: WorklistInfoInput[] = [];
    for (const plate of values.plates) {
      worklist.push({
        task: "Feed",
        details: isEB_FEED
          ? isYesterday(new Date(plate.dateCreated))
            ? "Day1"
            : ""
          : mystBarcode
          ? "MediaPosition|1"
          : "",
        aspirateVol: plate.aspirateVol!.toString(),
        dispenseVol: plate.dispenseVol!.toString(),
        sourcePlateType: plate.labwareTypeCode ?? "",
        sourcePlateBarcode: plate.plateBarcode ?? "",
      });
    }

    if (mystBarcode) {
      const plateCode = getPlateCodeFromPlateBarcode(sortedSourceBarcodes[0]);
      worklist.push(
        {
          task: "HarvestMediaToTubes",
          destinationPlateBarcode: mystBarcode,
        },
        {
          task: "SrcToMystMappingFileKeyword",
          details: `_${plateCode}_to_MYST_`,
        },
      );
    }

    return worklist;
  };

  const generateWorklist = async () => {
    // Don't hand the robot a worklist that silently lacks the MYST rows
    if (mystFlagError || (includeMystRack && !mystBarcode)) return;

    const methodReservationInput: WorklistToolMethodReservationInput = {
      fileName: values.fileName,
      methodId: selectedTask.methodId!,
      selectedSystem: selectedTask.systemId,
      sourcePlateInfo: values.plates.map((plate) => ({
        plateBarcode: plate.plateBarcode,
        plateType: plate.labwareTypeCode,
      })),
      destPlateInfo: [],
      worklist: buildWorklist(mystBarcode),
      reservationTime: new Date(selectedTask.startTimeScheduled),
      personalTaskId: selectedTask.taskId,
      mystPlateBarcode: mystBarcode,
    };

    const res = await reserveMethod({
      variables: {
        methodReservationInput,
      },
      refetchQueries: [
        OperationNames.Query.getMethodReservationByPersonalTaskId,
      ],
      // Keep Generate disabled until the dialog has switched to the reserved view
      awaitRefetchQueries: true,
    });
    downloadWorklist(res.data?.reserveMethod?.methodReservationId ?? 0);
  };

  const handleAspirateApplyToAll = () => {
    setValues(
      {
        ...values,
        plates: values.plates.map((p) => ({
          ...p,
          aspirateVol: aspirateApplyToAll,
        })) as typeof values.plates,
      },
      true,
    );
  };

  const handleDispenseApplyToAll = () => {
    setValues(
      {
        ...values,
        plates: values.plates.map((p) => ({
          ...p,
          dispenseVol: dispenseApplyToAll,
        })) as typeof values.plates,
      },
      true,
    );
  };

  const [aspirateApplyToAll, setAspirateApplyToAll] = useState(0);
  const [dispenseApplyToAll, setDispenseApplyToAll] = useState(0);
  return plates.length > 0 ? (
    <Paper sx={{ p: 2 }}>
      <FormikProvider value={formik}>
        <Form onSubmit={handleSubmit}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <NumberField
              size="small"
              sx={{ width: "35%" }}
              label="Aspirate Vol"
              value={aspirateApplyToAll}
              disabled={methodReserved || isEB_FEED}
              InputProps={{
                endAdornment: (
                  <Tooltip title="Apply to all plates">
                    <IconButton
                      disabled={methodReserved || isEB_FEED}
                      onClick={() => handleAspirateApplyToAll()}
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                  </Tooltip>
                ),
              }}
              onChange={(val) => setAspirateApplyToAll(parseInt(val) || 0)}
            />

            <NumberField
              size="small"
              sx={{ width: "35%" }}
              label="Dispense Vol"
              disabled={methodReserved || isEB_FEED}
              value={dispenseApplyToAll}
              InputProps={{
                endAdornment: (
                  <IconButton
                    disabled={methodReserved || isEB_FEED}
                    onClick={() => handleDispenseApplyToAll()}
                  >
                    <ArrowDownwardIcon />
                  </IconButton>
                ),
              }}
              onChange={(val) => setDispenseApplyToAll(parseInt(val) || 0)}
            />
          </div>
          <GeneralFeedTable
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            methodReserved={methodReserved}
            isEB_FEED={isEB_FEED}
          />
          {showMystBarcode && (
            <>
              <Typography
                variant="body1"
                color={mystMessageError ? "error" : undefined}
                sx={{ px: 2, py: 2 }}
              >
                {mystMessageError ?? (
                  <>
                    <u>
                      {methodReserved
                        ? "MYST Plate in Worklist"
                        : "Will Generate MYST Plate"}
                    </u>
                    {`: ${
                      displayedMystBarcode ??
                      (mystRackLoading ? "Loading..." : "")
                    }`}
                  </>
                )}
              </Typography>
              <Divider />
            </>
          )}
          <GeneralFeedFooter
            methodReserved={methodReserved}
            methodReservation={methodReservation}
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            isValid={
              isValid && !mystFlagError && (!includeMystRack || !!mystBarcode)
            }
            isSubmitting={isSubmitting}
            loading={loading || feedGroupQuery.loading || mystRackLoading}
          />
        </Form>
      </FormikProvider>
    </Paper>
  ) : (
    <></>
  );
};
